import { type CSSProperties, type PointerEvent, type ReactNode } from "react";
import { XIcon } from "lucide-react";

import type { FilterDefinitionType, FilterType } from "@/components/@types";
import { ReportsViewWidgetHeaderFilters } from "@/app/app/_/views/ReportsView/ReportsViewWidgetHeaderFilters";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { EnumColors } from "@/constants/values";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";
import { Typography } from "@/components/ui/typography";

type ReportsViewCardHeaderFiltersProps = {
  filters: FilterType[];
  filterDefinitions: Record<string, FilterDefinitionType>;
  onFiltersChange: (filters: FilterType[]) => void;
};

export type ReportsViewCardPrivacy = "public" | "private";

type ReportsViewCardAssembleProps = {
  /** Spread onto the widget header row for drag-and-drop (from `@dnd-kit/sortable`). */
  headerDragProps?: Record<string, unknown>;
  /**
   * The control that describes the card to an agent, drawn before the cross.
   * Only a report being assembled has one: a shared link is read, not asked.
   */
  describeNode?: ReactNode;
  /**
   * What the widget is asking, which is also what it is called. Only a report
   * being assembled shows it - a shared link is read, not asked.
   */
  titleNode?: ReactNode;
};

export type ReportsViewCardProps = {
  children: ReactNode;
  /** ReportsViewCard title in the header row. */
  title?: string | null;
  /** Per-widget filters rendered inline next to the title (excludes global report filters). */
  headerFilters?: ReportsViewCardHeaderFiltersProps | null;
  /** Custom header filter slot (e.g. shared public report widget filters). */
  headerFiltersNode?: ReactNode;
  /** Public / private badge shown in the header whenever provided. */
  privacy?: ReportsViewCardPrivacy | null;
  /** Title editing and header drag handle for layout assembly. */
  assemble?: ReportsViewCardAssembleProps | null;
  /** Removes the widget from the layout (shown as X next to header filters). */
  onRemove?: () => void;
  /** When true, body content extends to the card edges (for tables and trees). */
  contentFlush?: boolean;
  /** Card body height; defaults to viewport breakpoints (use container-driven height in dashboards). */
  height?: string | number;
  /** The card's frame, over its own: a page's edge or shadow for it. */
  className?: string;
  /** Its surface, over its own (``--report-card-surface`` and its hover). */
  style?: CSSProperties;
};

const PRIVACY_ENUM_PROPS: Record<
  ReportsViewCardPrivacy,
  { label: string; enumColor: (typeof EnumColors)[keyof typeof EnumColors] }
> = {
  private: { label: "Private", enumColor: EnumColors.GREY_1 },
  public: { label: "Public", enumColor: EnumColors.RED },
};

function preventDragStart(event: PointerEvent) {
  event.stopPropagation();
}

/**
 * Keep the closed hand for as long as the card is held.
 *
 * ``:active`` is only the element under the pointer, and a drag takes the
 * pointer away from it at once - to a layer of its own, carrying the card -
 * so the cursor opened again the moment the card started moving. The page
 * wears it instead, until the hand lets go, wherever that happens.
 */
function holdTheGrabbingCursor() {
  const page = document.body;
  const wasCursor = page.style.cursor;
  const wasSelect = page.style.userSelect;
  page.style.cursor = "grabbing";
  page.style.userSelect = "none";

  const letGo = () => {
    page.style.cursor = wasCursor;
    page.style.userSelect = wasSelect;
    window.removeEventListener("pointerup", letGo);
    window.removeEventListener("pointercancel", letGo);
  };
  window.addEventListener("pointerup", letGo);
  window.addEventListener("pointercancel", letGo);
}

/**
 * What can be done to the card, at the end of its header.
 *
 * Two plain buttons side by side: nothing here is a setting being switched on,
 * so neither wears a filled surface, and with no surfaces to join there is
 * nothing for a group to hold together.
 */
function ReportsViewCardActions({
  describe,
  onRemove,
}: {
  describe?: ReactNode;
  onRemove: () => void;
}) {
  return (
    <div
      className="flex items-center gap-0.5 shrink-0"
      onPointerDown={preventDragStart}
    >
      {describe}
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label="Remove widget"
        data-testid="widget-remove"
        className="shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <XIcon size={16} />
      </Button>
    </div>
  );
}

function ReportsViewCardHeader({
  title,
  headerFilters,
  headerFiltersNode,
  privacy,
  assemble,
  onRemove,
}: Pick<
  ReportsViewCardProps,
  | "title"
  | "headerFilters"
  | "headerFiltersNode"
  | "privacy"
  | "assemble"
  | "onRemove"
>) {
  const hasTitle = Boolean(title?.trim()) || assemble?.titleNode != null;
  const hasHeaderFilters = headerFilters != null || headerFiltersNode != null;
  const hasPrivacy = privacy != null;
  const hasRemove = onRemove != null;

  if (!hasTitle && !hasHeaderFilters && !hasPrivacy && !hasRemove) {
    return null;
  }

  const privacyEnum = hasPrivacy ? PRIVACY_ENUM_PROPS[privacy!] : null;

  return (
    <div
      // From the top, not the middle: a widget's title is the sentence it
      // asks, which on a narrow card runs to four lines, and centred against
      // all of them the cross hung half-way down the card's edge. What can
      // be done to a card sits in its corner, beside the first line.
      className="flex items-start gap-2 min-h-7 w-full min-w-0"
    >
      <div className="flex items-center gap-1.5 min-w-0 min-h-7 flex-1">
        {assemble?.titleNode ? (
          // What the widget is asking, which is also what it is called: it
          // sits where the title would, because it is the title.
          <div className="min-w-0 flex-1">{assemble.titleNode}</div>
        ) : (
          hasTitle && (
            <Typography
              level="title-md"
              className="truncate font-semibold pl-1 pr-1 min-w-0 flex-1 text-muted-foreground"
            >
              {title}
            </Typography>
          )
        )}
        {privacyEnum && (
          <SimpleEnum
            label={privacyEnum.label}
            enumColor={privacyEnum.enumColor}
            size="md"
          />
        )}
      </div>
      {(hasHeaderFilters || hasRemove) && (
        <div
          className="flex items-center gap-1 shrink-0 min-w-0 max-w-[60%] ml-auto"
          onPointerDown={preventDragStart}
        >
          {headerFiltersNode ? (
            <div className="min-w-0 overflow-visible">{headerFiltersNode}</div>
          ) : (
            headerFilters && (
              <div className="min-w-0 overflow-visible">
                <ReportsViewWidgetHeaderFilters
                  filters={headerFilters.filters}
                  filterDefinitions={headerFilters.filterDefinitions}
                  onChange={headerFilters.onFiltersChange}
                />
              </div>
            )
          )}
          {hasRemove && (
            <ReportsViewCardActions
              describe={assemble?.describeNode}
              onRemove={onRemove!}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function ReportsViewCard({
  children,
  title = null,
  headerFilters = null,
  headerFiltersNode = null,
  privacy = null,
  assemble = null,
  onRemove,
  contentFlush = false,
  height,
  className,
  style,
}: ReportsViewCardProps) {
  const hasTitleSection =
    Boolean(title?.trim()) ||
    headerFilters != null ||
    headerFiltersNode != null ||
    privacy != null ||
    assemble != null ||
    onRemove != null;

  const headerDragProps = assemble?.headerDragProps;
  const isHeaderDraggable = headerDragProps != null;
  const colors = useLayoutColors();

  return (
    <div
      data-testid="report-card"
      className={cn(
        "relative isolate rounded-lg w-full min-w-0 min-h-0 overflow-hidden flex flex-col",
        // As tall as it is told, else as tall as the screen allows.
        height == null && "h-[240px] md:h-[400px]",
        // The card is as tall as it was told to be. It used to grow instead,
        // which handed its height to whatever was holding it - and on the
        // dashboard that is a grid row sized by its contents, so the card and
        // the row each waited on the other. Chrome broke the circle on the
        // content; Safari broke it on nothing, and left a narrow dashboard as
        // a column of bare headers with the charts spilling out of them.
        "shrink-0",
        "bg-(--report-card-surface)",
        className,
      )}
      style={
        {
          height: height ?? undefined,
          // Recessed rather than raised: the card sits a shade off the canvas
          // and carries no shadow, so the grid reads as tiles (a page close in
          // shade to its cards draws a divider around them through
          // ``className``). The surface is published as a variable because the
          // grids, trees and sticky headers inside a widget have to paint the
          // same colour; the shade itself is per-scheme (see
          // ``useLayoutColors``), and a page may override both through
          // ``style``, which is spread last.
          "--report-card-surface": colors.reportCard,
          "--report-card-surface-hover": colors.reportCardHover,
          ...style,
        } as CSSProperties
      }
    >
      {hasTitleSection && (
        <div
          {...(headerDragProps as Record<string, never>)}
          onPointerDown={(event: React.PointerEvent<HTMLDivElement>) => {
            (
              headerDragProps as
                | { onPointerDown?: (event: React.PointerEvent) => void }
                | undefined
            )?.onPointerDown?.(event);
            if (isHeaderDraggable) holdTheGrabbingCursor();
          }}
          // A mouse's drag only: a finger on the header scrolls the
          // report, and picks the card up by holding still.
          // Where the hand closes, the cursor closes with it.
          // A press that is a drag is not a press that selects: the
          // header used to leave text highlighted across the page
          // wherever a drag did not start.
          className={cn(
            "shrink-0 pl-4 pr-4 pt-4 pb-3 [border-bottom-width:1px] [border-bottom-style:solid] border-border overflow-visible",
            isHeaderDraggable &&
              "cursor-grab [user-select:none] pointer-fine:touch-none active:cursor-grabbing",
          )}
        >
          <ReportsViewCardHeader
            title={title}
            headerFilters={headerFilters}
            headerFiltersNode={headerFiltersNode}
            privacy={privacy}
            assemble={assemble}
            onRemove={onRemove}
          />
        </div>
      )}
      <div
        className={cn(
          "flex-1 min-h-0 min-w-0 w-full flex flex-col",
          contentFlush ? "overflow-y-hidden" : "overflow-y-auto",
          "overflow-x-hidden",
          contentFlush
            ? ""
            : hasTitleSection
              ? "pl-4 pr-4 pt-3 pb-3"
              : "pl-4 pr-4 pt-4 pb-4",
        )}
      >
        {children}
      </div>
    </div>
  );
}
